import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import styles from './EditJob.module.css';
import FormDateField from '../../../components/UI/UpdatedFormUI/FormDateField/FormDateField';
import SlateRichTextEditor from '../../../components/UI/SlateRichTextEditor/SlateRichTextEditor';

// Helper function to convert Slate nodes to HTML
const slateToHtml = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) {
        return '';
    }
    
    let html = '';
    
    nodes.forEach(node => {
        if (node.type === 'paragraph') {
            html += '<p>';
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => {
                    let text = child.text || '';
                    if (child.bold) text = `<strong>${text}</strong>`;
                    if (child.italic) text = `<em>${text}</em>`;
                    if (child.underline) text = `<u>${text}</u>`;
                    if (child.strikethrough) text = `<s>${text}</s>`;
                    html += text;
                });
            }
            html += '</p>';
        } else if (node.type === 'bulleted-list') {
            html += '<ul>';
            if (node.children && Array.isArray(node.children)) {
                node.children.forEach(listItem => {
                    html += '<li>';
                    if (listItem.children && Array.isArray(listItem.children)) {
                        listItem.children.forEach(child => {
                            let text = child.text || '';
                            if (child.bold) text = `<strong>${text}</strong>`;
                            if (child.italic) text = `<em>${text}</em>`;
                            if (child.underline) text = `<u>${text}</u>`;
                            if (child.strikethrough) text = `<s>${text}</s>`;
                            html += text;
                        });
                    }
                    html += '</li>';
                });
            }
            html += '</ul>';
        }
    });
    
    return html;
};

function JobSummary({ formik, setActiveSteps, activeSteps }) {
    const { t } = useTranslation();
    
    useEffect(() => {
        // Set default deadline date to +30 days if not already set
        if (!formik.values.endDate) {
            const date = new Date();
            date.setDate(date.getDate() + 30);
            formik.setFieldValue('endDate', date.toISOString().split('T')[0]);
        }

        // Handle description initialization
        // No need for complex HTML parsing here as the SlateRichTextEditor now handles it
        if (!formik.values.description) {
            // If no description, set an empty default
            formik.setFieldValue('description', [{ type: 'paragraph', children: [{ text: '' }] }]);
            formik.setFieldValue('descriptionHtml', '');
        }
        else if (Array.isArray(formik.values.description)) {
            // If already in Slate format, make sure HTML is set
            formik.setFieldValue('descriptionHtml', slateToHtml(formik.values.description));
        }
        // We'll let the SlateRichTextEditor component handle HTML parsing
    }, []);

    // Calculate the word count from Slate value
    const getWordCount = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return 0;
        
        let text = '';
        // Walk through all nodes to extract text
        const getTextFromNode = (node) => {
            if (typeof node.text === 'string') {
                text += node.text + ' ';
            } else if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => getTextFromNode(child));
            }
        };
        
        nodes.forEach(node => getTextFromNode(node));
        
        // Count words by splitting on whitespace and filtering out empty strings
        return text.trim().split(/\s+/).filter(Boolean).length;
    };

    // Get character count for validation
    const getCharacterCount = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return 0;
        
        let text = '';
        // Walk through all nodes to extract text without spaces
        const getTextFromNode = (node) => {
            if (typeof node.text === 'string') {
                text += node.text;
            } else if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => getTextFromNode(child));
            }
        };
        
        nodes.forEach(node => getTextFromNode(node));
        
        return text.length;
    };

    const wordCount = formik.values.description ? getWordCount(formik.values.description) : 0;
    const charCount = formik.values.description ? getCharacterCount(formik.values.description) : 0;
    const minWords = 20;
    const maxWords = 300;
    const minChars = 20;
    const isDescriptionValid = wordCount >= minWords && wordCount <= maxWords && charCount >= minChars;

    // Create a serializer to get plain text for submission
    const getPlainTextFromNodes = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) return '';
        
        let text = '';
        const getTextFromNode = (node) => {
            if (typeof node.text === 'string') {
                text += node.text;
            } else if (node.children && Array.isArray(node.children)) {
                node.children.forEach(child => getTextFromNode(child));
                if (node.type === 'paragraph' || node.type === 'list-item') {
                    text += '\n';
                }
            }
        };
        
        nodes.forEach(node => getTextFromNode(node));
        return text;
    };

    // Handle changes from the Slate editor
    const handleEditorChange = (value) => {
        // Save the Slate structure for editing
        formik.setFieldValue('description', value.target.value);
        
        // Convert to HTML for rendering in other parts of the application
        const htmlContent = slateToHtml(value.target.value);
        formik.setFieldValue('descriptionHtml', htmlContent);
        
        // If you need the plain text version for form submission
        const plainText = getPlainTextFromNodes(value.target.value);
        formik.setFieldValue('descriptionPlainText', plainText);
    };

    return (
        <>
            <div className={`${styles.row} ${styles.rowContainer}`}>
                <div className={styles.summaryContainer}>
                    <p className={styles.summarylabel}>
                        {t("JobDetails.Job_Description")} <span>({minWords}-{maxWords} {t('general.UI.words')})</span>
                    </p>
                    <div className={styles.editorContainer}>
                        <SlateRichTextEditor
                            onChange={handleEditorChange}
                            placeholder={t("JobDetails.Placeholder_Description")}
                            name="description"
                            onBlur={formik.handleBlur}
                            className={styles.richTextEditor}
                            value={formik.values.description}
                            error={
                                (charCount > 0 && charCount < minChars && t('general.UI.minLength', { min: minChars })) ||
                                (wordCount > maxWords && t('general.UI.maxLength', { max: maxWords }))
                            }
                        />
                        <div className={styles.wordCount}>
                            {wordCount} {t('general.UI.words')}
                        </div>
                    </div>
                </div>
            </div>
            <div className={`${styles.row} ${styles.summaryrow}`}>
                <div className={styles.summaryfullWidth}>
                    <p className={styles.summarylabel}>{t("JobDetails.Application_Deadline")}</p>
                    <div className={styles.summarydateFieldContainer}>
                        <FormDateField
                            name="endDate"
                            placeholder={t("JobDetails.End_Date")}
                            hasBorder
                            onChange={formik.handleChange}
                            value={formik.values.endDate}
                            error={formik.errors.endDate}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default JobSummary;
